import {
  formatUnits,
  parseUnits,
  readContract,
  writeContractAndWait,
  zeroAddress,
} from "../viem";
import config from "../../config/config";
import MafiaLotteryHallAbi from "../../abis/MafiaLotteryHall.json";

export type LotteryRoundOnchain = {
  startTime: number;
  endTime: number;
  /** raw wei string */
  totalEntriesWei: string;
  /** formatted cash amount (18 decimals) */
  totalEntriesCash: number;
  winner: string;
  ended: boolean;
};

export type LotteryRoundFinishInfoOnchain = {
  roundId: number;
  winner: string;
  /** formatted cash amount (18 decimals) */
  totalPrizeCash: number;
  /** formatted cash amount (18 decimals) */
  ownerFeeCash: number;
  isOwnerWithdraw: boolean;
};

const address = (config as any).lotteryHall as string;
const abi = MafiaLotteryHallAbi;

export const getCurrentRoundId = async () => {
  const id = (await readContract({
    address,
    abi,
    functionName: "currentRoundId",
  })) as any;
  return Number(id);
};

export const getOwnerFeePercent = async () => {
  const p = (await readContract({
    address,
    abi,
    functionName: "ownerFeePercent",
  })) as any;
  return Number(p);
};

export const getOwnerTotalProfit = async (): Promise<number> => {
  const profitWei = (await readContract({
    address,
    abi,
    functionName: "ownerTotalProfit",
  })) as any;
  return Number(formatUnits(profitWei || "0", 18));
};

export const getRoundDuration = async () => {
  const d = (await readContract({
    address,
    abi,
    functionName: "roundDuration",
  })) as any;
  return Number(d);
};

export const getInitialPrize = async (): Promise<number> => {
  const prizeWei = (await readContract({
    address,
    abi,
    functionName: "initialPrize",
  })) as any;
  return Number(formatUnits(prizeWei || "0", 18));
};

export const getCurrentPrize = async (): Promise<number> => {
  const prizeWei = (await readContract({
    address,
    abi,
    functionName: "getCurrentPrize",
  })) as any;
  return Number(formatUnits(prizeWei || "0", 18));
};

export const getLotteryOwner = async () => {
  return (await readContract({
    address,
    abi,
    functionName: "lotteryOwner",
  })) as any as string;
};

export const getRound = async (roundId: number): Promise<LotteryRoundOnchain> => {
  const r = (await readContract({
    address,
    abi,
    functionName: "rounds",
    args: [roundId],
  })) as any;
  const totalEntriesWei = (r?.totalEntries ?? r?.[2] ?? "0") as string;
  return {
    startTime: Number(r?.startTime ?? r?.[0] ?? 0),
    endTime: Number(r?.endTime ?? r?.[1] ?? 0),
    totalEntriesWei,
    totalEntriesCash: Number(formatUnits(totalEntriesWei || "0", 18)),
    winner: (r?.winner ?? r?.[3] ?? zeroAddress) as string,
    ended: Boolean(r?.ended ?? r?.[4] ?? false),
  };
};

export const getUserEntries = async (
  roundId: number,
  user: string
): Promise<number> => {
  const amtWei = (await readContract({
    address,
    abi,
    functionName: "userEntries",
    args: [roundId, user],
  })) as any;
  return Number(formatUnits(amtWei || "0", 18));
};

export const getParticipantsCount = async (roundId: number) => {
  const count = (await readContract({
    address,
    abi,
    functionName: "getParticipantsCount",
    args: [roundId],
  })) as any;
  return Number(count);
};

export const getParticipants = async (
  roundId: number,
  startIndex: number,
  length: number
) => {
  const res = (await readContract({
    address,
    abi,
    functionName: "getParticipants",
    args: [roundId, startIndex, length],
  })) as any;
  const list = (res?.list ?? res?.[0] ?? []) as string[];
  const amountsWei = (res?.amounts ?? res?.[1] ?? []) as string[];
  const amountsCash = amountsWei.map((v) =>
    Number(formatUnits(v || "0", 18))
  );
  return { list, amountsWei, amountsCash };
};

export const getRoundFinishInfos = async (
  startIndex: number,
  length: number
): Promise<LotteryRoundFinishInfoOnchain[]> => {
  const res = (await readContract({
    address,
    abi,
    functionName: "getRoundFinishInfos",
    args: [startIndex, length],
  })) as any;
  // Solidity returns a direct array for `RoundFinishInfo[]` in most web3 decoders.
  // Some decoders also attach numeric keys, so handle both.
  const list = (Array.isArray(res) ? res : res?.[0] ?? res ?? []) as any[];
  return (list || []).map((x: any) => {
    const totalPrizeWei = (x?.totalPrize ?? x?.[2] ?? "0") as string;
    const ownerFeeWei = (x?.ownerFee ?? x?.[3] ?? "0") as string;
    return {
      roundId: Number(x?.roundId ?? x?.[0] ?? 0),
      winner: (x?.winner ?? x?.[1] ?? zeroAddress) as string,
      totalPrizeCash: Number(formatUnits(totalPrizeWei || "0", 18)),
      ownerFeeCash: Number(formatUnits(ownerFeeWei || "0", 18)),
      isOwnerWithdraw: Boolean(x?.isOwnerWithdraw ?? x?.[4] ?? false),
    } satisfies LotteryRoundFinishInfoOnchain;
  });
};

export const enterLottery = async (amountCash: number, account: string) => {
  const amountWei = parseUnits(amountCash.toString(), 18);
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "enter",
    args: [amountWei],
    account,
  });
  return tx;
};

export const drawWinner = async (account: string) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "drawWinner",
    account,
  });
  return tx;
};

export const ownerWithdraw = async (account: string) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "ownerWithdraw",
    account,
  });
  return tx;
};
